import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { AbstractBaseSection } from './abstract-base-section';
import type { EmbodimentOutline } from '../../apis/create-embodiment-outline-api';
import { GenEmbodimentApi } from '../../apis/gen-embodiment-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { SSE2StreamProvider } from '../../utils';
import type { SpecificationStreamContent, SpecificationStreamProvider } from '../../types/specification';
import { SpecificationContentType, SpecificationOperateType } from '../../enums';
import type { SpecificationParagraphWithData } from '@/domains/specification/types/specification-gen-content';
import { SpecificationEmbodimentOutlineService } from '@/domains/specification/services/specification-embodiment-outline-service';

@injectable()
export class EmbodimentItemSection extends AbstractBaseSection<SpecificationParagraphWithData<string, EmbodimentOutline>> {
    readonly contentType = SpecificationContentType.EmbodimentItem;
    private _currentProvider: SpecificationStreamProvider<SpecificationStreamContent<string>> | undefined;

    constructor(
        @inject(GenEmbodimentApi)
        private readonly _genEmbodimentApi: GenEmbodimentApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(SpecificationEmbodimentOutlineService)
        private readonly _outlineService: SpecificationEmbodimentOutlineService,
    ) {
        super();
    }

    private _currentOutline: EmbodimentOutline | undefined;

    async prepare(outline: EmbodimentOutline) {
        this._currentOutline = outline;
    }

    async generate(regenerate?: boolean) {
        const sse = this._genEmbodimentApi.genEmbodimentFromOutline({
            task_id: this._currentTaskService.taskId,
            embodiment_number: this._currentOutline?.embodiment_number!,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });

        const provider = SSE2StreamProvider<string, string>(
            sse,
            (content) => content || '',
        );

        this._currentProvider = provider;

        await provider.subscribe(({ status, content }) => {
            this.updateContent({
                type: this.contentType,
                content,
                status: status!,
                data: this._currentOutline,
            });
        }).promise;

        return this.currentContent!;
    }

    async afterGenerate(regenerate?: boolean) {
        if (!regenerate) return;

        await this.save(this.currentContent!);
    }

    async save(paragraph: SpecificationParagraphWithData<string, EmbodimentOutline>) {
        if (!paragraph?.data?.embodiment_number) return;
        return this._genEmbodimentApi.saveEmbodimentFromOutline({
            task_id: this._currentTaskService.taskId,
            embodiment_number: paragraph.data.embodiment_number,
            type: paragraph.data?.type || '',
            claim_numbers: paragraph?.data?.claim_numbers || [],
            text: paragraph.content || '',
        });
    }

    async remove(paragraph: SpecificationParagraphWithData<string, EmbodimentOutline>) {
        if (!paragraph?.data?.embodiment_number) return;
        await Promise.all([
            this._genEmbodimentApi.deleteEmbodimentWithOutline({
                task_id: this._currentTaskService.taskId,
                embodiment_number: paragraph.data.embodiment_number,
            }),
            this._outlineService.remove(paragraph.data),
        ]);
    }

    stop() {
        if (this._currentProvider) {
            this._currentProvider.abort();
            this._currentProvider = undefined;
            this.currentContent = undefined;
        }
    }
}
